import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { QButton, QStack, useTheme } from 'quotient/vNext';
import { useCallback, useState, useRef, useEffect } from 'react';
import {
  CartesianGrid,
  DefaultTooltipContent,
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts/types/component/Tooltip';

import { useLineChart } from './hooks/useLineChart';
import { LineChartContainer } from './LineChartContainer.styles';

import { LineChartProps } from '../../types';
import {
  getChartColors,
  displayLegend,
  formatLabel,
  X_AXIS_LABEL_ANGLE_STRAIGHT,
  X_AXIS_LABEL_ANGLE_ROTATED,
  CHAR_WIDTH_PX,
  formatDateString,
  isISODateString,
} from '../../utils/chartUtils';
import { ChartLegend } from '../BarChart/ChartLegend';
import { useAutoRotateLabels } from '../../hooks/useAutoRotateLabels';

export const LineChart = ({
  targetLine,
  onClick,
  hideAxes,
  angleLabels = 'auto',
  showDots = 'auto',
  xAxisDateFormat,
  ...chartProps
}: LineChartProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
  const [chartWidth, setChartWidth] = useState(0);

  // Use the auto-rotate labels hook for intelligent label rotation
  // Estimate yAxisWidth for initial calculation (typical Y-axis is 40-60px)
  const { containerRef, effectiveAngleLabels } = useAutoRotateLabels({
    data: chartProps.data,
    xAxisKeys: chartProps.xAxisKeys,
    yAxisWidth: 50, // Estimated Y-axis width; hook will recalculate on resize
    hideXAxis: hideAxes,
    hideYAxis: hideAxes,
    isHorizontal: false, // LineChart is always vertical
    angleLabels: angleLabels === 'auto' ? false : angleLabels,
    showFullLabels: false,
  });

  // Track chart width for dot density calculations
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setChartWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [containerRef]);

  /**
   * Format x-axis values with date handling
   * - If xAxisDateFormat is provided (user's locale formatter), use it
   * - Otherwise, auto-detect ISO dates and apply default 'short' format
   * - Fall back to formatLabel for non-dates
   */
  const formatXAxisValue = useCallback(
    (value: any): string => {
      // Use user's locale-based formatter if provided
      if (xAxisDateFormat) {
        return xAxisDateFormat(value);
      }

      // Auto-detect dates and apply default format
      if (isISODateString(value)) {
        return formatDateString(value, 'short');
      }

      // Fall back to standard label formatting
      return formatLabel(value);
    },
    [xAxisDateFormat],
  );

  /**
   * Determine label rotation based on angleLabels prop and hook calculation
   * - If explicitly set (true/false), use that value
   * - If 'auto', use the hook's calculated value
   */
  const shouldRotateLabels = angleLabels === 'auto' ? effectiveAngleLabels : angleLabels;

  /**
   * Calculate max label length for bottom margin calculations
   * Uses formatted label lengths for accurate height estimation when rotated
   */
  const maxLabelLength =
    chartProps.data.length > 0 && shouldRotateLabels
      ? Math.max(
          ...chartProps.data.map((datum) => {
            const value = datum[chartProps.xAxisKeys[0]];
            return formatXAxisValue(value).length;
          }),
        )
      : 0;

  const { data, formatValue, xAxisKeys, xAxisLabels, xAxisLabelSpacing, yAxisKeys, yAxisLabels, yAxisWidth } =
    useLineChart({ ...chartProps, angleLabels: shouldRotateLabels });

  /**
   * Calculate bottom margin for x-axis labels
   * Adds extra space for rotated labels based on their length
   */
  const calculateBottomMargin = () => {
    if (hideAxes) {
      return 10;
    }

    if (!shouldRotateLabels) {
      return xAxisLabelSpacing(xAxisKeys[0]);
    }

    // When rotated at 45°, labels extend vertically by: width * sin(45°)
    // Add some base padding for the axis line and tick marks
    const rotatedLabelHeight = Math.ceil(maxLabelLength * CHAR_WIDTH_PX * Math.sin(Math.PI / 4));
    const basePadding = 5; // Space for axis line and tick marks

    return rotatedLabelHeight + basePadding;
  };

  /**
   * Calculate whether to show dots and their size based on data density
   */
  const calculateDotSettings = () => {
    // Explicit control overrides auto calculation
    if (showDots === false) {
      return { show: false, radius: 0, activeRadius: 0 };
    }
    if (showDots === true) {
      return { show: true, radius: 4, activeRadius: 6 };
    }

    // Auto mode: calculate based on data density
    if (chartWidth === 0 || data.length === 0) {
      return { show: true, radius: 4, activeRadius: 6 };
    }

    const pixelsPerPoint = chartWidth / data.length;

    // Thresholds for dot behavior
    const HIDE_DOTS_THRESHOLD = 3; // Hide when less than 3px per point
    const MIN_DOT_SIZE_THRESHOLD = 8; // Start shrinking when less than 8px per point
    const MAX_DOT_RADIUS = 4;
    const MIN_DOT_RADIUS = 2;

    if (pixelsPerPoint < HIDE_DOTS_THRESHOLD) {
      // Too dense - hide dots but keep active dot for interaction
      return { show: false, radius: 0, activeRadius: 6 };
    }

    if (pixelsPerPoint < MIN_DOT_SIZE_THRESHOLD) {
      // Moderately dense - scale down dot size
      const scale = (pixelsPerPoint - HIDE_DOTS_THRESHOLD) / (MIN_DOT_SIZE_THRESHOLD - HIDE_DOTS_THRESHOLD);
      const radius = MIN_DOT_RADIUS + (MAX_DOT_RADIUS - MIN_DOT_RADIUS) * scale;
      return {
        show: true,
        radius: Math.floor(radius),
        activeRadius: 6,
      };
    }

    // Sparse data - show full-size dots
    return { show: true, radius: MAX_DOT_RADIUS, activeRadius: 6 };
  };

  const dotSettings = calculateDotSettings();

  /**
   * Recharts tooltip handler for line charts
   */
  const handleTooltipHover = useCallback(
    ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
      if (active && payload?.length) {
        // Get the x-axis label for better context
        const xAxisLabel = xAxisLabels && xAxisLabels.length > 0 ? xAxisLabels[0] : 'Value';

        // When hideAxes is true, use the actual data value instead of array index
        let displayLabel = label;
        if (hideAxes && payload[0]?.payload) {
          const actualValue = payload[0].payload[xAxisKeys[0]];
          if (actualValue !== undefined) {
            displayLabel = actualValue;
          }
        }

        // Use the same formatting logic as the x-axis labels
        const formattedLabel = `${xAxisLabel}: ${formatXAxisValue(displayLabel)}`;

        return (
          <DefaultTooltipContent
            label={formattedLabel}
            payload={payload.map((item: any) => ({
              ...item,
              value: formatValue(item.value),
            }))}
          />
        );
      }
      return null;
    },
    [formatValue, xAxisLabels, hideAxes, xAxisKeys, formatXAxisValue],
  );

  return (
    <LineChartContainer ref={containerRef} data-testid="line-chart">
      {displayLegend(yAxisKeys) && (
        <QStack alignItems="flex-start" direction="row" spacing={1} sx={{ mb: 4 }}>
          <QButton
            color="primary"
            data-testid="view-legend-button"
            size="small"
            startIcon={faList}
            variant="text"
            onClick={() => setIsLegendModalOpen(true)}
          >
            View legend
          </QButton>
        </QStack>
      )}
      <ResponsiveContainer key={yAxisKeys.length}>
        <RechartsLineChart
          data={data}
          margin={{
            bottom: calculateBottomMargin(),
            right: hideAxes ? 10 : 50,
            top: hideAxes ? 10 : 0,
            left: hideAxes ? 10 : 0,
          }}
          onClick={onClick}
        >
          {!hideAxes && <CartesianGrid stroke={theme.palette.neutral.primary[100]} strokeDasharray="3 3" />}
          {!hideAxes && (
            <XAxis
              angle={shouldRotateLabels ? X_AXIS_LABEL_ANGLE_ROTATED : X_AXIS_LABEL_ANGLE_STRAIGHT}
              dataKey={xAxisKeys[0]}
              padding={{ left: 10, right: 50 }}
              stroke={theme.palette.neutral.primary[700]}
              tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'start' }}
              tickFormatter={formatXAxisValue}
              tickLine={false}
            />
          )}
          {!hideAxes && (
            <YAxis
              minTickGap={0.5}
              padding={{ top: 10 }}
              stroke={theme.palette.neutral.primary[700]}
              tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'end' }}
              tickFormatter={formatValue}
              tickLine={false}
              width={yAxisWidth}
            />
          )}
          <Tooltip content={handleTooltipHover} />

          {/* Render line series */}
          {yAxisKeys?.map((dataKey, seriesIndex) => {
            const colors = chartColors[seriesIndex % chartColors.length];

            // Determine dot configuration based on hideAxes and dotSettings
            let dotConfig;
            if (hideAxes) {
              dotConfig = false;
            } else if (dotSettings.show) {
              dotConfig = { fill: colors.default, strokeWidth: 2, r: dotSettings.radius };
            } else {
              dotConfig = false;
            }

            let activeDotConfig;
            if (hideAxes) {
              activeDotConfig = false;
            } else if (dotSettings.activeRadius > 0) {
              activeDotConfig = { r: dotSettings.activeRadius, strokeWidth: 1, fill: colors.hover };
            } else {
              activeDotConfig = false;
            }

            return (
              <Line
                activeDot={activeDotConfig}
                dataKey={dataKey}
                dot={dotConfig}
                key={dataKey}
                name={yAxisLabels[seriesIndex]}
                stroke={colors.default}
                strokeWidth={2}
              />
            );
          })}

          {/* Render target line */}
          {targetLine !== undefined && targetLine !== null && (
            <>
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.common.white}
                strokeLinecap="round"
                strokeWidth={4}
                y={targetLine}
              />
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.denotive.positive[600]}
                strokeLinecap="round"
                strokeWidth={2}
                y={targetLine}
              />
            </>
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
      <ChartLegend
        chartColors={chartColors}
        labels={yAxisLabels}
        open={isLegendModalOpen}
        onClose={() => setIsLegendModalOpen(false)}
      />
    </LineChartContainer>
  );
};
